import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { DEFAULT_PATHS } from '../routing/paths.ts';
import { useOrgModuleAccess } from '../hooks/useOrgModuleAccess.ts';
import { getWidgetDefinition } from './analytics/widgetRegistry.tsx';

/**
 * Reporting is on when the platform flag is set, or the user turned on the
 * local preview override (`/preview/reporting/enable`). Shared by the
 * sidebar and `ReportingRoute` so the link and the route agree.
 */
export function isReportingEnabled(moduleAccess: { reporting_enabled?: boolean } | undefined): boolean {
  if (moduleAccess?.reporting_enabled) return true;
  try {
    return localStorage.getItem('skeptics_reporting_preview') === 'true';
  } catch {
    return false;
  }
}

/**
 * Route guard for reporting pages: redirects to the landing page while
 * reporting is off. With `allowOperationalWidgets`, a `?widget=` deep link to
 * a widget marked `availableWithoutReporting` (the PBC tracker) still opens,
 * since that is where users do the work, not just read reports.
 */
const ReportingRoute: React.FC<{ children: React.ReactNode; allowOperationalWidgets?: boolean }> = ({
  children,
  allowOperationalWidgets = false,
}) => {
  const { data: moduleAccess, isLoading } = useOrgModuleAccess();
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get('widget');
  const operationalFocus =
    allowOperationalWidgets && !!focusKey && !!getWidgetDefinition(focusKey)?.availableWithoutReporting;

  if (isLoading) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center px-8 py-12 text-sm text-gray-500">Loading…</div>
    );
  }

  if (!isReportingEnabled(moduleAccess) && !operationalFocus) {
    return <Navigate to={DEFAULT_PATHS.TENANT_LANDING} replace />;
  }

  return <>{children}</>;
};

export default ReportingRoute;
